//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { EthAddr } from '@shared/SchemaTypes'
import React, { ReactElement } from 'react'

import { TrendingUp } from '@mui/icons-material'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import {
  Button,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  Typography,
  useTheme,
} from '@mui/material'
import IconButton from '@mui/material/IconButton'
import { ERC20Wei, aprToDisplay, erc20WeiIsSignificant, erc20WeiToCommaDecString, pptToUSDC } from '@shared/conversions'

import {
  PoolAccountCTO,
  PoolBaseAccountCTO,
  PoolBaseOverviewCTO,
  PoolDynamicAccountCTO,
  PoolDynamicOverviewCTO,
  PoolFlexAccountCTO,
  PoolFlexOverviewCTO,
  PoolOverviewCTO,
} from '@shared/sharedTypes'
import { Link, useNavigate } from 'react-router-dom'
import ViewLogo from '@react/components/elements/ViewLogo'
import CashOutIcon from '@react/components/icons/CashOutIcon'
import DownLeftArrowIcon from '@react/components/icons/DownLeftArrowIcon'
import ReinvestIcon from '@react/components/icons/ReinvestIcon'
import { OtStoreState, useOtChainState, useOtSelector } from '@react/redux/store'

import { PoolTypeEnum } from '@shared/Enums'
import { ETH_ADDR_ZERO } from '@shared/SchemaTypes'
import { buildLenderPoolSummary } from './lenderUtils'
import { buildPills } from '@react/components/elements/InterestPills'

function InvestmentRow(
  poolBaseAccountCTO: PoolBaseAccountCTO,
  poolBaseOverviewCTO: PoolBaseOverviewCTO,
  theme: Theme,

  navigate: any
): ReactElement {
  let matureDate
  let maturityDateType
  let pills: React.ReactElement | undefined = undefined
  let dollars

  switch (poolBaseOverviewCTO.poolType) {
    case PoolTypeEnum.Term: {
      const poolAccountCTO = poolBaseAccountCTO as PoolAccountCTO
      const poolOverviewCTO = poolBaseOverviewCTO as PoolOverviewCTO
      const investorSummary = buildLenderPoolSummary(poolOverviewCTO, poolAccountCTO)
      matureDate = new Date(investorSummary.matureEthDate * 1e3)
      maturityDateType = poolOverviewCTO.activeLoanState.loanAddr === ETH_ADDR_ZERO ? 'Indicative' : 'Active'

      if (Number(poolAccountCTO.requestedAssetsOf) > 0) {
        pills = (
          <>
            <Chip icon={<DownLeftArrowIcon />} label="Withdraw Requested" />
            <Chip
              sx={{ backgroundColor: theme.palette.accent2.subtle }}
              icon={<CashOutIcon />}
              label={matureDate.toLocaleDateString()}
            />
          </>
        )
      } else {
        pills = (
          <>
            <Chip
              sx={{ backgroundColor: theme.palette.info.subtle, marginRight: 2 }}
              icon={<ReinvestIcon />}
              label={matureDate.toLocaleDateString()}
            />
            <Chip
              sx={{ backgroundColor: theme.palette.success.subtle, marginRight: 2 }}
              icon={<TrendingUp />}
              label={aprToDisplay(poolOverviewCTO.activeLoanState.finalizedApr) + '% APY'}
            />
            {poolOverviewCTO.requestedLoanState.loanAddr !== ETH_ADDR_ZERO && (
              <Chip
                sx={{ backgroundColor: theme.palette.accent1.subtle }}
                icon={<TrendingUp />}
                label={`Indicative ${aprToDisplay(poolOverviewCTO?.requestedLoanState?.indicativeApr)}% APY`}
              />
            )}
          </>
        )
      }

      dollars = pptToUSDC(poolBaseAccountCTO?.tokenBalance, poolOverviewCTO?.exchangeRateAtMaturity)
      break
    }
    case PoolTypeEnum.FlexibleRate: {
      const poolAccountCTOFlex = poolBaseAccountCTO as PoolFlexAccountCTO
      const poolOverviewCTOFlex = poolBaseOverviewCTO as PoolFlexOverviewCTO
      dollars = poolAccountCTOFlex.accountOutstandingLoanPrincipals
      pills = buildPills(poolOverviewCTOFlex, theme)
      break
    }
    case PoolTypeEnum.Dynamic: {
      const poolAccountCTODynamic = poolBaseAccountCTO as PoolDynamicAccountCTO
      const poolOverviewCTODynamic = poolBaseOverviewCTO as PoolDynamicOverviewCTO
      dollars = poolAccountCTODynamic.principalEarningInterest
      pills = buildPills(poolOverviewCTODynamic, theme)
      break
    }
    default:
      break
  }

  return (
    <TableRow
      key={poolBaseOverviewCTO.poolAddr}
      onClick={(_) => {
        navigate(`/pools/${poolBaseOverviewCTO?.poolAddr}`)
      }}
      sx={{ textDecoration: 'none' }}
    >
      <TableCell key={'logo'}>
        <ViewLogo style={{ width: '30px', height: '30px' }} poolAddr={poolBaseOverviewCTO.poolAddr} />
      </TableCell>
      <TableCell width={'100%'} key={'name'} align="left">
        <Typography alignContent={'left'} variant="body2" component="div" style={{ textDecoration: 'none' }}>
          {poolBaseOverviewCTO.name}
        </Typography>
        {poolBaseOverviewCTO.poolType === PoolTypeEnum.FlexibleRate && (
          <Typography variant="captionMuted" color={theme.palette.fg.muted} gutterBottom component="div">
            Flexible Term
          </Typography>
        )}
        {poolBaseOverviewCTO.poolType === PoolTypeEnum.Term && matureDate && (
          <Typography variant="small" color={theme.palette.fg.muted} gutterBottom component="div">
            Maturing {matureDate.toLocaleDateString()} {maturityDateType === 'Indicative' && '(Indicative)'}
          </Typography>
        )}
        {pills}
      </TableCell>
      <TableCell key={'right'} align="right">
        <Typography display={'inline'} variant={'body2'}>
          {erc20WeiToCommaDecString(dollars as ERC20Wei)}
        </Typography>
        <Typography display={'inline'} variant={'caption'}>
          {' '}
          {poolBaseOverviewCTO.liquidityTokenSymbol}{' '}
        </Typography>
      </TableCell>
      <TableCell key={'nav'} align="right">
        <IconButton
          component={Link}
          to={`/pools/${poolBaseOverviewCTO.poolAddr ?? ''}`}
          aria-label="expand row"
          size="small"
        >
          <ArrowForwardIosIcon fontSize="small" />
        </IconButton>
      </TableCell>
    </TableRow>
  )
}

export default function LenderInvestmentListSummary(props: { limit?: number }): ReactElement {
  const poolAccountCTOs = useOtSelector((state: OtStoreState) => state.poolState.poolAccountCTOs)
  const poolOverviewCTOs = useOtSelector((state: OtStoreState) => state.poolState.poolOverviewCTOs)

  const {
    userConnection: { accountAddr },
  } = useOtChainState()
  const theme = useTheme()
  const [poolCTOList, setPoolCTOList] = React.useState<PoolBaseAccountCTO[]>([])

  const navigate = useNavigate()

  React.useEffect(() => {
    if (poolAccountCTOs && accountAddr) {
      const poolCTOList = Object.values(poolAccountCTOs)
        .map((poolAllAccountCTO: Record<EthAddr, PoolBaseAccountCTO>) => {
          return poolAllAccountCTO[accountAddr]
        })
        .filter((poolCTO: PoolBaseAccountCTO) => {
          return erc20WeiIsSignificant(poolCTO?.tokenBalance ?? '0')
        })
        .sort((a: PoolBaseAccountCTO, b: PoolBaseAccountCTO) => Number(BigInt(b.tokenBalance) - BigInt(a.tokenBalance)))
      setPoolCTOList(poolCTOList)
    }
  }, [poolAccountCTOs, accountAddr])

  if (Object.keys(poolAccountCTOs).length === 0) {
    return <div data-testid="pool-list-empty"></div>
  }
  return (
    <Paper
      sx={{
        marginTop: 4,
        marginBottom: 4,
        padding: '24px',
        borderRadius: 4,
        backgroundColor: theme.palette.background.paper,
      }}
    >
      <Typography align="left" sx={{ display: 'inline', float: 'left', fontWeight: 'bold' }} variant={'h4'}>
        Investments
      </Typography>
      {props.limit && (
        <Button
          variant="text"
          component={Link}
          to={'/investments'}
          sx={{ display: 'inline', float: 'right', color: theme.palette.primary.main }}
        >
          <Typography sx={{ color: theme.palette.accent1.emphasis }} variant={'bodyBold'}>
            View All
          </Typography>
        </Button>
      )}
      <TableContainer sx={{ maxHeight: 800 }}>
        <Table stickyHeader aria-label="sticky table">
          <TableHead>
            <TableRow>
              <TableCell style={{ width: '5%' }}></TableCell>
              <TableCell style={{ width: '45%' }} align="right"></TableCell>
              <TableCell style={{ width: '43%' }} align="left"></TableCell>
              <TableCell style={{ width: '2%' }} align="left"></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {poolCTOList
              .filter((poolAccountCTO: PoolBaseAccountCTO) => {
                if (poolAccountCTO && poolOverviewCTOs[poolAccountCTO.poolAddr]) {
                  return true
                }
                return false
              })
              .map((poolAccountCTO: PoolBaseAccountCTO) => {
                return InvestmentRow(poolAccountCTO, poolOverviewCTOs[poolAccountCTO.poolAddr], theme, navigate)
              })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  )
}
